import type { ReactNode } from "react";
import { Spinner } from "./Spinner";

/**
 * The content of a button that has a busy state ("Entrar" → "Entrando").
 *
 * Browser auto-translate (Chrome) rewrites text nodes in place. If React swaps the button's children
 * between two fragments, the nodes React tracks no longer match the DOM, and the old translated
 * label is left behind next to the new one ("Enter the portal Entering Enter the portal"). Here the
 * DOM shape never changes: the spinner and icon are always present and toggled with `hidden`, and
 * the label is a keyed element, so a change replaces the whole element rather than a text node.
 */
export function ButtonLabel({ busy, busyLabel, label, icon, iconFirst = false }: {
  busy: boolean;
  /** Shown while busy, e.g. "Entrando". */
  busyLabel: string;
  /** Shown otherwise, e.g. "Entrar al portal". */
  label: ReactNode;
  /** Optional icon shown only when idle. */
  icon?: ReactNode;
  /** Place the icon before the label (the default is after). */
  iconFirst?: boolean;
}) {
  const iconSlot = icon ? <span className="button-icon" hidden={busy}>{icon}</span> : null;
  return (
    <span className="button-label">
      <span className="button-spinner" hidden={!busy}><Spinner /></span>
      {iconFirst && iconSlot}
      <span className="button-text" key={busy ? "busy" : "idle"}>{busy ? busyLabel : label}</span>
      {!iconFirst && iconSlot}
    </span>
  );
}
